<template>
    <div>
        <div :class="croppieClass">
            <div id="croppieContainer" ref="croppieContainer"></div>
        </div>
        <input
            ref="fileInput"
            type="file"
            style="display: none"
            accept="image/png,image/jpg,image/jpeg"
            @change="selectImage($event)"
        />

        <common-buttons>
            <common-button
                type="secondary"
                :isProcessing="isLoading"
                :label="$t('common.microcopy.select')"
                @click="$refs.fileInput.click()"
            ></common-button>
            <common-button
                type="primary"
                :isProcessing="isLoading"
                :label="$t('common.microcopy.save')"
                :isDisabled="!isImageSelected"
                @click="uploadFile"
            ></common-button>
        </common-buttons>
    </div>
</template>

<script>
    import Croppie from 'croppie'
    import 'croppie/croppie.css' // import the croppie css manually

    export default {
        props: {
            boundary: { type: Object, default: () => ({ width: 400, height: 400 }) },
            enableExif: { type: Boolean, default: false },
            enableOrientation: { type: Boolean, default: false },
            enableResize: { type: Boolean, default: false },
            enableZoom: { type: Boolean, default: true },
            enforceBoundary: { type: Boolean, default: false },
            mouseWheelZoom: { type: [Boolean, String], default: true },
            showZoomer: { type: Boolean, default: true },
            croppieInitialized: { type: Function, default: function () {} },
            uploadApi: { type: Function, default: function () {} },
            viewport: {
                type: Object,
                default: function () {
                    return {
                        width: 350,
                        height: 350,
                        type: 'circle',
                    }
                },
            },
            minZoom: { type: Number, default: 0.1 },
            maxZoom: { type: Number, default: 2.5 },
            defaultPhoto: { type: String, default: null },
        },

        data() {
            return {
                croppie: null,
                isImageSelected: false,
                isLoading: false,
                placeholderImage: require('../../assets/images/image-placeholder.png'),
                isCoverHidden: true,
            }
        },

        computed: {
            croppieClass() {
                return {
                    'image-cropper__disabled': !this.isImageSelected,
                }
            },
        },

        methods: {
            initCroppie() {
                let el = this.$refs.croppieContainer

                let options = {
                    enableExif: this.enableExif,
                    enableOrientation: this.enableOrientation,
                    enableZoom: this.enableZoom,
                    enableResize: this.enableResize,
                    enforceBoundary: this.enforceBoundary,
                    mouseWheelZoom: this.mouseWheelZoom,
                    viewport: this.viewport,
                    showZoomer: this.showZoomer,
                    minZoom: this.minZoom,
                    maxZoom: this.maxZoom,
                    customClass: 'croppie',
                }
                if (this.boundary) {
                    options.boundary = this.boundary
                }

                el.addEventListener('update', (ev) => {
                    this.$emit('update', ev.detail)
                })

                this.croppie = new Croppie(el, options)

                this.croppieInitialized()
                this.croppie.bind({
                    url: this.placeholderImage,
                })
            },
            destroy() {
                this.croppie.destroy()
            },
            get(cb) {
                if (cb) {
                    cb(this.croppie.get())
                } else {
                    return this.croppie.get()
                }
            },
            rotate(angle) {
                this.croppie.rotate(angle)
            },
            setZoom(value) {
                this.croppie.setZoom(value)
            },
            result(options, cb) {
                if (!options) options = { type: 'base64' }
                return this.croppie.result(options).then((output) => {
                    if (!cb) {
                        this.$emit('result', output)
                    } else {
                        cb(output)
                    }
                    return output
                })
            },
            refresh() {
                this.croppie.destroy()
                this.initCroppie()
            },
            fileSelected(event) {
                this.onFileSelected(event)
                this.$refs.fileInput.value = ''
            },
            selectImage(e) {
                const files = e.target.files || e.dataTransfer.files
                if (!files.length) return

                const reader = new FileReader()
                reader.onload = (e) => {
                    this.croppie
                        .bind({
                            url: e.target.result,
                        })
                        .then(() => {
                            this.isImageSelected = true
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.invalid_image'))
                        })
                }

                reader.readAsDataURL(files[0])
                this.isCoverHidden = false
            },
            uploadFile() {
                this.isLoading = true
                Promise.all([
                    this.result({ size: { width: 100, height: 100 } }),
                    this.result({ size: { width: 400, height: 400 } }),
                ])
                    .then(([thumbnail, original]) => {
                        this.uploadApi({ main: original.split(`,`)[1], avatar: thumbnail.split(`,`)[1] })
                            ?.then(() => {
                                this.$toast.success(this.$t('common.microcopy.success'))
                                this.emitEvent({ name: 'photo-uploaded' })
                            })
                            .finally(() => (this.isLoading = false))
                    })
                    .catch(() => (this.isLoading = false))
            },
        },

        mounted() {
            this.initCroppie()
        },
    }
</script>

<style lang="scss" scoped>
    .image-cropper {
        &__disabled {
            pointer-events: none;
            user-select: none;
        }
    }
</style>
